<template>
  <div v-if="submission">
    <div v-if="showDownload || allowPublish" class="mb-2 d-flex align-center">
      <v-btn v-if="showDownload" x-small color="secondary" @click="downloadJson" class="mr-2">Download JSON</v-btn>
      <v-btn v-if="allowPublish && !isEditing" x-small color="secondary" @click="startEditing" class="mr-2">Edit Message</v-btn>
      <v-btn v-if="allowPublish && !isEditing" x-small color="secondary" @click="openRepublishDialog" class="mr-2" :loading="republishing">Republish Message</v-btn>
      <v-btn v-if="allowPublish && isEditing" x-small color="secondary" @click="cancelEditing" class="mr-2">Cancel Edit</v-btn>
      <v-btn v-if="allowPublish && isEditing" x-small color="secondary" @click="openPublishDialog" class="mr-2" :disabled="!isValidJson">Publish to Service Bus</v-btn>
      <span v-if="isEditing && !isValidJson" class="red--text text-caption ml-2">Invalid JSON</span>
    </div>
    <MonacoEditor
      height="700px"
      language="json"
      :value="displayValue"
      :options="editorOptions"
      @change="onEditorChange"
    ></MonacoEditor>

    <!-- Publish Confirmation Dialog -->
    <v-dialog v-model="showPublishConfirm" max-width="500">
      <v-card>
        <v-card-title class="headline">Confirm Publish</v-card-title>
        <v-card-text>
          <p>Are you sure you want to publish this message to Service Bus?</p>
          <div v-if="!hasKnownTopicName">
            <p class="orange--text text--darken-2">Topic name was derived from message type. Please verify or enter the correct topic name:</p>
            <v-text-field
              v-model="editableTopicName"
              label="Topic Name"
              outlined
              dense
              hint="Topic name should be lowercase (e.g., quotedocumentsaved-v1)"
              persistent-hint
              :rules="[v => /^[a-z][a-z0-9]*(-v\d+)?$/.test(v) || 'Topic name should be lowercase with optional -v1, -v2 suffix']"
            ></v-text-field>
          </div>
          <p v-else><strong>Topic:</strong> {{ effectiveTopicName }}</p>
          <p class="red--text">This action cannot be undone.</p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" text @click="showPublishConfirm = false">Cancel</v-btn>
          <v-btn color="success" @click="publishMessage" :loading="publishing" :disabled="!editableTopicName">Publish</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Republish Confirmation Dialog -->
    <v-dialog v-model="showRepublishConfirm" max-width="500">
      <v-card>
        <v-card-title class="headline">Confirm Republish</v-card-title>
        <v-card-text>
          <p>Are you sure you want to republish this message to Service Bus?</p>
          <div v-if="!hasKnownTopicName">
            <p class="orange--text text--darken-2">Topic name was derived from message type. Please verify or enter the correct topic name:</p>
            <v-text-field
              v-model="editableTopicName"
              label="Topic Name"
              outlined
              dense
              hint="Topic name should be lowercase (e.g., quotedocumentsaved-v1)"
              persistent-hint
              :rules="[v => /^[a-z][a-z0-9]*(-v\d+)?$/.test(v) || 'Topic name should be lowercase with optional -v1, -v2 suffix']"
            ></v-text-field>
          </div>
          <p v-else><strong>Topic:</strong> {{ effectiveTopicName }}</p>
          <p>This will fetch the original message and republish it as-is.</p>
          <p class="red--text">This action cannot be undone.</p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" text @click="showRepublishConfirm = false">Cancel</v-btn>
          <v-btn color="success" @click="republishMessage" :loading="republishing" :disabled="!editableTopicName">Republish</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Result Snackbar -->
    <v-snackbar v-model="showSnackbar" :color="snackbarColor" :timeout="5000">
      {{ snackbarMessage }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="showSnackbar = false">Close</v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import MonacoEditor from 'monaco-editor-vue';
import { MessageQueueAdminService } from '@/api/MessageQueueAdminService';

export default {
  name: 'SubmissionJson',
  components: {
    MonacoEditor
  },
  data () {
    return {
      isEditing: false,
      editedContent: '',
      isValidJson: true,
      showPublishConfirm: false,
      showRepublishConfirm: false,
      publishing: false,
      republishing: false,
      showSnackbar: false,
      snackbarMessage: '',
      snackbarColor: 'success',
      editableTopicName: ''
    };
  },
  props: {
    submission: Object,
    showDownload: {
      type: Boolean,
      default: false
    },
    fileName: {
      type: String,
      default: 'message'
    },
    allowPublish: {
      type: Boolean,
      default: false
    },
    submissionId: {
      type: String,
      default: ''
    },
    submissionNumber: {
      type: String,
      default: ''
    },
    typeName: {
      type: String,
      default: ''
    },
    messageId: {
      type: String,
      default: ''
    },
    topicName: {
      type: String,
      default: null
    },
    derivedTopicName: {
      type: String,
      default: ''
    }
  },
  computed: {
    submissionString () {
      if (!this.submission) return "";
      else return JSON.stringify(this.submission, null, 2);
    },
    displayValue () {
      return this.isEditing ? this.editedContent : this.submissionString;
    },
    editorOptions () {
      return {
        colorDecorators: true,
        readOnly: !this.isEditing
      };
    },
    hasKnownTopicName () {
      return this.topicName !== null && this.topicName !== '';
    },
    effectiveTopicName () {
      // Use the known topicName if available, otherwise use the editable/derived one
      return this.hasKnownTopicName ? this.topicName : this.editableTopicName;
    }
  },
  methods: {
    openPublishDialog () {
      // Set the editable topic name to the known or derived value
      this.editableTopicName = this.hasKnownTopicName ? this.topicName : this.derivedTopicName;
      this.showPublishConfirm = true;
    },
    openRepublishDialog () {
      // Set the editable topic name to the known or derived value
      this.editableTopicName = this.hasKnownTopicName ? this.topicName : this.derivedTopicName;
      this.showRepublishConfirm = true;
    },
    startEditing () {
      // Parse the submission and update MessageId with a new GUID
      let messageData = JSON.parse(this.submissionString);
      if (messageData.MessageId !== undefined) {
        messageData.MessageId = crypto.randomUUID();
      }
      this.editedContent = JSON.stringify(messageData, null, 2);
      this.isEditing = true;
      this.isValidJson = true;
    },
    cancelEditing () {
      this.isEditing = false;
      this.editedContent = '';
      this.isValidJson = true;
    },
    onEditorChange (value) {
      if (this.isEditing) {
        this.editedContent = value;
        try {
          JSON.parse(value);
          this.isValidJson = true;
        } catch {
          this.isValidJson = false;
        }
      }
    },
    async publishMessage () {
      this.publishing = true;
      try {
        const messageData = JSON.parse(this.editedContent);

        await MessageQueueAdminService.publishMessage({
          topic: this.editableTopicName,
          message: messageData
        });

        this.showPublishConfirm = false;
        this.snackbarColor = 'success';
        this.snackbarMessage = `Message successfully published to topic: ${this.editableTopicName}`;
        this.showSnackbar = true;
        this.cancelEditing();
      } catch (error) {
        this.snackbarColor = 'error';
        this.snackbarMessage = `Failed to publish message: ${error.message || error}`;
        this.showSnackbar = true;
      } finally {
        this.publishing = false;
      }
    },
    async republishMessage () {
      this.republishing = true;
      try {
        await MessageQueueAdminService.republishMessage({
          topic: this.editableTopicName,
          submissionId: this.submissionId,
          submissionNumber: this.submissionNumber,
          typeName: this.typeName,
          messageId: this.messageId
        });

        this.showRepublishConfirm = false;
        this.snackbarColor = 'success';
        this.snackbarMessage = `Message successfully republished to topic: ${this.editableTopicName}`;
        this.showSnackbar = true;
      } catch (error) {
        this.snackbarColor = 'error';
        this.snackbarMessage = `Failed to republish message: ${error.message || error}`;
        this.showSnackbar = true;
      } finally {
        this.republishing = false;
      }
    },
    downloadJson () {
      // Use the known topic name or derived topic name
      const topicForDownload = this.hasKnownTopicName ? this.topicName : this.derivedTopicName;

      // Generate new GUIDs
      const newGuid = crypto.randomUUID();
      const traceGuid = crypto.randomUUID().replace(/-/g, '');

      const wrappedMessage = {
        data: this.submission,
        datacontenttype: "application/json",
        id: newGuid,
        pubsubname: "pubsub",
        source: "",
        specversion: "1.0",
        time: new Date().toISOString(),
        topic: topicForDownload,
        traceid: `00-${traceGuid}-0000000000000000-01`,
        traceparent: `00-${traceGuid}-0000000000000000-01`,
        tracestate: "",
        type: "com.dapr.event.sent"
      };

      const jsonString = JSON.stringify(wrappedMessage, null, 2);
      const blob = new Blob([jsonString], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${this.fileName}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
  }
};
</script>
